import React, { lazy } from 'react';
import {Routes, Route, Navigate, useLocation, Location} from 'react-router-dom';
import { SignUpContextProvider } from "../contexts/SignUpContext";
import { AccountType } from "../models/AccountType";
import {MenuOption} from "../models/MenuOption";
import { useUser } from "../contexts/UserContext";
import RouteSuspense from "../components/RouteSuspense";
import { ModalOverlayProvider } from "../contexts/ModalOverlayContext";
import { hasModalBackground, resolveModalBackground } from "../hooks/useSafeBackNavigation";
import ClassroomModal from "../components/modals/add/ClassroomModal";
import AddSubject from "../components/modals/add/AddSubject";
import AssignStudentEntry from "../components/modals/add/AssignStudentEntry";
import ModalOverlayEntry from "../components/routing/ModalOverlayEntry";
import ModalOverlayRoute from "../components/routing/ModalOverlayRoute";

const AppBase = lazy(() => import("../pages/AppBase"));
const SignInPage = lazy(() => import("../pages/auth/SignInPage"));
const SignUpPage = lazy(() => import("../pages/auth/SignUpPage"));
const Classrooms = lazy(() => import("../pages/classroom/Classrooms"));
const DemoClassroom = lazy(() => import("../pages/classroom/DemoClassroom"));
const DemoGuardianPreview = lazy(() => import("../pages/classroom/DemoGuardianPreview"));
const Classroom = lazy(() => import("../pages/classroom/Classroom"));
const StaffDashboard = lazy(() => import("../pages/staff/StaffDashboard"));
const Teachers = lazy(() => import("../pages/staff/Teachers"));
const Students = lazy(() => import("../pages/staff/Students"));
const Guardians = lazy(() => import("../pages/staff/Guardians"));
const Settings = lazy(() => import("../pages/staff/Settings"));
const GeneralSettings = lazy(() => import("../pages/settings/GeneralSettings"));
const ResultSettings = lazy(() => import("../pages/settings/ResultSettings"));
const AddUserModal = lazy(() => import("../components/modals/add/AddUserModal"));
const GradeBook = lazy(() => import("../pages/staff/GradeBook"));
const GenerateResults = lazy(() => import("../pages/staff/GenerateResults"));
const FirstReportMissionShell = lazy(() => import("../components/onboarding/FirstReportMissionShell"));
const User = lazy(() => import("../pages/staff/User"));
const AssignTeacher = lazy(() => import("../components/modals/add/AssignTeacher"));
const ClassroomStudents = lazy(() => import("../pages/classroom/ClassroomStudents"));
const ClassroomSubjects = lazy(() => import("../pages/classroom/ClassroomSubjects"));
const ShareResult = lazy(() => import("../components/modals/ShareResult"));
const ShareResultPreview = lazy(() => import("../pages/sharing/ShareResultPreview"));
const PublicReport = lazy(() => import("../pages/sharing/PublicReport"));
const AuthUser = lazy(() => import("../pages/AuthUser"));
const BillingSettings = lazy(() => import("../pages/settings/BillingSettings"));
const PermissionsSettings = lazy(() => import("../pages/settings/PermissionsSettings"));
const UserModal = lazy(() => import("../components/modals/add/UserModal"));
const Remove = lazy(() => import("../components/modals/Remove"));
const TeacherInvitation = lazy(() => import("../pages/auth/invite/TeacherInvitation"));
const AcceptInvite = lazy(() => import("../pages/auth/AcceptInvite"));
const ForgotPasswordPage = lazy(() => import("../pages/auth/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("../pages/auth/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("../pages/auth/VerifyEmailPage"));
const ManageStudentClassroomsModal = lazy(() => import("../components/modals/ManageStudentClassroomsModal"));
const ManageLinkedUsersModal = lazy(() => import("../components/modals/ManageLinkedUsersModal"));
const ProfileAccessGuard = lazy(() => import("../components/routing/ProfileAccessGuard"));
const ProfileEditModal = lazy(() => import("../components/routing/ProfileEditModal"));
const ModalRouteRedirect = lazy(() => import("../components/routing/ModalRouteRedirect"));

const noop = () => {};

const StaffOnly = ({ children }: { children: React.ReactNode }) => {
    const { user } = useUser();
    if (user && (user.type === AccountType.STUDENT || user.type === AccountType.GUARDIAN)) {
        return <Navigate to="/dashboard" replace />;
    }
    return <>{children}</>;
};

const AdminOnly = ({
    children,
    redirectTo = '/dashboard',
}: {
    children: React.ReactNode;
    redirectTo?: string;
}) => {
    const { user } = useUser();
    if (user && user.type !== AccountType.ADMIN) {
        return <Navigate to={redirectTo} replace />;
    }
    return <>{children}</>;
};

const renderRoutes = (location: Location, background: Location | undefined) => {
    return (
        <>
            <Routes location={background || location}>
                <Route path="/create-account" element={
                    <RouteSuspense>
                        <SignUpContextProvider>
                            <SignUpPage />
                        </SignUpContextProvider>
                    </RouteSuspense>
                } />
                <Route path="/login" element={<RouteSuspense><SignInPage /></RouteSuspense>} />
                <Route path="/forgot-password" element={<RouteSuspense><ForgotPasswordPage /></RouteSuspense>} />
                <Route path="/reset-password" element={<RouteSuspense><ResetPasswordPage /></RouteSuspense>} />
                <Route path="/verify-email" element={<RouteSuspense><VerifyEmailPage /></RouteSuspense>} />
                <Route path="/r/:token" element={<RouteSuspense><PublicReport /></RouteSuspense>} />
                <Route path="teacher/invitation" element={<RouteSuspense><TeacherInvitation/></RouteSuspense>} />
                <Route path="invite/accept" element={<RouteSuspense><AcceptInvite/></RouteSuspense>} />

                <Route element={
                    <RouteSuspense>
                        <FirstReportMissionShell />
                    </RouteSuspense>
                }>
                    <Route path="gradebook" element={
                        <RouteSuspense>
                            <AuthUser><StaffOnly><GradeBook /></StaffOnly></AuthUser>
                        </RouteSuspense>
                    } />
                    <Route path="results/generate" element={
                        <RouteSuspense><AuthUser><GenerateResults /></AuthUser></RouteSuspense>
                    } />
                </Route>
                <Route
                    path="results/:classroomId/:studentId/share"
                    element={
                        <RouteSuspense>
                            <ModalRouteRedirect fallbackBackground={{ pathname: '/results/generate' }}>
                                <AuthUser><ShareResult /></AuthUser>
                            </ModalRouteRedirect>
                        </RouteSuspense>
                    }
                />
                <Route path="share-result/:classroomId/:studentId/preview" element={
                    <RouteSuspense><AuthUser><ShareResultPreview /></AuthUser></RouteSuspense>
                } />
                <Route path="/class/add" element={
                    <ModalOverlayRoute fallbackBackground={{ pathname: '/gradebook' }}>
                        <AdminOnly><ClassroomModal onAddedClassroom={noop} /></AdminOnly>
                    </ModalOverlayRoute>
                } />
                <Route path="/class/:id/add-subject" element={
                    <ModalOverlayRoute fallbackBackground={{ pathname: '/gradebook' }}>
                        <AddSubject />
                    </ModalOverlayRoute>
                } />
                <Route path="/class/:id/assign-student" element={
                    <ModalOverlayRoute fallbackBackground={{ pathname: '/gradebook' }}>
                        <AssignStudentEntry />
                    </ModalOverlayRoute>
                } />
                <Route path="/class/:id/settings" element={
                    <RouteSuspense>
                        <ModalOverlayRoute fallbackBackground={{ pathname: '/gradebook' }}>
                            <AdminOnly><ClassroomModal onAddedClassroom={noop} /></AdminOnly>
                        </ModalOverlayRoute>
                    </RouteSuspense>
                } />
                <Route path="/class/:id/assign-teacher" element={
                    <RouteSuspense>
                        <ModalOverlayRoute fallbackBackground={{ pathname: '/gradebook' }}>
                            <AdminOnly><AssignTeacher /></AdminOnly>
                        </ModalOverlayRoute>
                    </RouteSuspense>
                } />
                <Route path="/class/:id/delete" element={
                    <RouteSuspense>
                        <ModalOverlayRoute fallbackBackground={{ pathname: '/gradebook' }}>
                            <AdminOnly><Remove type={MenuOption.CLASSES} /></AdminOnly>
                        </ModalOverlayRoute>
                    </RouteSuspense>
                } />
                <Route path="/" element={
                    <RouteSuspense><AppBase>{null}</AppBase></RouteSuspense>
                }>
                    <Route index element={<Navigate to='gradebook' />}></Route>
                    <Route path="dashboard" element={<StaffDashboard />} />
                    <Route path="profile" element={<User selfProfile />} />
                    <Route path="profile/edit" element={<ProfileEditModal />} />
                    <Route path="classes" element={<Classrooms />} />
                    <Route path="demo-classroom" element={<AdminOnly><DemoClassroom /></AdminOnly>} />
                    <Route path="demo-classroom/guardian-preview" element={<AdminOnly><DemoGuardianPreview /></AdminOnly>} />
                    <Route path="class/:id/*"
                        element={
                            <RouteSuspense>
                                <Classroom>
                                    <Routes>
                                        <Route index element={<Navigate to='students' replace />} />
                                        <Route path="students" element={<RouteSuspense><ClassroomStudents/></RouteSuspense>} />
                                        <Route path="subjects" element={<RouteSuspense><ClassroomSubjects /></RouteSuspense>} />
                                        <Route path="stats" element={<>In Progress</>} />
                                    </Routes>
                                </Classroom>
                            </RouteSuspense>
                        } />
                    <Route path="teachers" element={<AdminOnly><Teachers /></AdminOnly>} />
                    <Route path="teacher/add" element={<AdminOnly><AddUserModal userType={AccountType.TEACHER} /></AdminOnly>} />
                    <Route path="teacher/:id/*"
                           element={
                               <ProfileAccessGuard expectedType={AccountType.TEACHER}>
                                   <Routes>
                                       <Route path="" element={<User userType={AccountType.TEACHER} />} />
                                       <Route path="/edit" element={<UserModal userType={AccountType.TEACHER} />} />
                                       <Route path="/delete" element={<Remove type={MenuOption.TEACHERS}/>} />
                                   </Routes>
                               </ProfileAccessGuard>
                           } />

                    <Route path="students" element={<AdminOnly><Students /></AdminOnly>} />
                    <Route path="student/add" element={<AdminOnly><AddUserModal userType={AccountType.STUDENT} /></AdminOnly>} />
                    <Route path="student/:id/*"
                           element={
                               <ProfileAccessGuard expectedType={AccountType.STUDENT}>
                                   <Routes>
                                       <Route path="" element={<User userType={AccountType.STUDENT} />} />
                                       <Route path="/classrooms" element={<ManageStudentClassroomsModal />} />
                                       <Route path="/guardians" element={<ManageLinkedUsersModal mode="student-guardians" />} />
                                       <Route path="/edit" element={<UserModal userType={AccountType.STUDENT} attachedUserType={AccountType.GUARDIAN}/>} />
                                       <Route path="/delete" element={<Remove type={MenuOption.STUDENTS}/>} />
                                   </Routes>
                               </ProfileAccessGuard>
                           } />

                    <Route path="guardians" element={<AdminOnly><Guardians /></AdminOnly>} />
                    <Route path="guardian/add" element={<AdminOnly><AddUserModal userType={AccountType.GUARDIAN} /></AdminOnly>} />
                    <Route path="guardian/:id/*"
                           element={
                               <ProfileAccessGuard expectedType={AccountType.GUARDIAN}>
                                   <Routes>
                                       <Route path="" element={<User userType={AccountType.GUARDIAN}/>} />
                                       <Route path="/wards" element={<ManageLinkedUsersModal mode="guardian-students" />} />
                                       <Route path="/edit" element={<UserModal userType={AccountType.GUARDIAN} attachedUserType={AccountType.STUDENT}/>} />
                                       <Route path="/delete" element={<Remove type={MenuOption.GUARDIANS}/>} />
                                   </Routes>
                               </ProfileAccessGuard>
                           } />

                    <Route path="settings/*"
                        element={
                            <Settings>
                                <Routes>
                                    <Route index element={<Navigate to='general' />} />
                                    <Route path="/general" element={<GeneralSettings />} />
                                    <Route path="/results" element={<ResultSettings />} />
                                    <Route path="/billing" element={<AdminOnly redirectTo="../general"><BillingSettings /></AdminOnly>} />
                                    <Route path="/permissions" element={<AdminOnly redirectTo="../general"><PermissionsSettings /></AdminOnly>} />
                                </Routes>
                            </Settings>
                        } />
                </Route>
            </Routes>

            {background && (
                <Routes location={location}>
                    {/*
                      Every route opened via navigateTo(..., true) must be registered here.
                      Otherwise the overlay layer renders nothing (blank screen).
                    */}
                    <Route path="results/:classroomId/:studentId/share" element={
                        <RouteSuspense><AuthUser><ShareResult /></AuthUser></RouteSuspense>
                    } />
                    <Route path="share-result/:classroomId/:studentId/preview" element={
                        <RouteSuspense><AuthUser><ShareResultPreview /></AuthUser></RouteSuspense>
                    } />

                    <Route path="/class/:id/assign-student" element={
                        <ModalOverlayEntry><AssignStudentEntry /></ModalOverlayEntry>
                    } />
                    <Route path="/class/:id/assign-teacher" element={
                        <RouteSuspense>
                            <ModalOverlayEntry><AdminOnly><AssignTeacher /></AdminOnly></ModalOverlayEntry>
                        </RouteSuspense>
                    } />
                    <Route path="/class/:id/add-subject" element={
                        <ModalOverlayEntry><AddSubject /></ModalOverlayEntry>
                    } />
                    <Route path="/class/:id/settings" element={
                        <RouteSuspense>
                            <ModalOverlayEntry>
                                <AdminOnly><ClassroomModal onAddedClassroom={noop} /></AdminOnly>
                            </ModalOverlayEntry>
                        </RouteSuspense>
                    } />
                    <Route path="/class/:id/delete" element={
                        <RouteSuspense>
                            <ModalOverlayEntry><AdminOnly><Remove/></AdminOnly></ModalOverlayEntry>
                        </RouteSuspense>
                    } />
                    <Route path="/class/add" element={
                        <ModalOverlayEntry>
                            <AdminOnly><ClassroomModal onAddedClassroom={noop} /></AdminOnly>
                        </ModalOverlayEntry>
                    } />

                    <Route path="teacher/add" element={<RouteSuspense><AdminOnly><AddUserModal userType={AccountType.TEACHER} /></AdminOnly></RouteSuspense>} />
                    <Route path="student/add" element={<RouteSuspense><AdminOnly><AddUserModal userType={AccountType.STUDENT} /></AdminOnly></RouteSuspense>} />
                    <Route path="guardian/add" element={<RouteSuspense><AdminOnly><AddUserModal userType={AccountType.GUARDIAN} /></AdminOnly></RouteSuspense>} />

                    <Route path="teacher/:id/edit" element={<RouteSuspense><AdminOnly><UserModal userType={AccountType.TEACHER} /></AdminOnly></RouteSuspense>} />
                    <Route path="student/:id/classrooms" element={<RouteSuspense><AdminOnly><ManageStudentClassroomsModal /></AdminOnly></RouteSuspense>} />
                    <Route path="student/:id/guardians" element={<RouteSuspense><AdminOnly><ManageLinkedUsersModal mode="student-guardians" /></AdminOnly></RouteSuspense>} />
                    <Route path="student/:id/edit" element={<RouteSuspense><AdminOnly><UserModal userType={AccountType.STUDENT} attachedUserType={AccountType.GUARDIAN}/></AdminOnly></RouteSuspense>} />
                    <Route path="guardian/:id/wards" element={<RouteSuspense><AdminOnly><ManageLinkedUsersModal mode="guardian-students" /></AdminOnly></RouteSuspense>} />
                    <Route path="guardian/:id/edit" element={<RouteSuspense><AdminOnly><UserModal userType={AccountType.GUARDIAN} attachedUserType={AccountType.STUDENT}/></AdminOnly></RouteSuspense>} />

                    <Route path="teacher/:id/delete" element={<RouteSuspense><AdminOnly><Remove type={MenuOption.TEACHERS}/></AdminOnly></RouteSuspense>} />
                    <Route path="student/:id/delete" element={<RouteSuspense><AdminOnly><Remove type={MenuOption.STUDENTS}/></AdminOnly></RouteSuspense>} />
                    <Route path="guardian/:id/delete" element={<RouteSuspense><AdminOnly><Remove type={MenuOption.GUARDIANS}/></AdminOnly></RouteSuspense>} />

                    <Route path="profile/edit" element={<RouteSuspense><ProfileEditModal /></RouteSuspense>} />
                </Routes>
            )}
        </>
    );
};

const RoutesConfig = () => {
    const location = useLocation();
    const background = hasModalBackground(location) ? resolveModalBackground(location) : undefined;

    return (
        <ModalOverlayProvider>
            {renderRoutes(location, background)}
        </ModalOverlayProvider>
    );
};

export default RoutesConfig;
